import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { getCurrency, formatPriceCurrency, addDays } from 'utils/utils';
import { DealModel, PckRoom } from 'ts/package/interfaces/PackageOfflineResponse';
import Rating from 'components/ui/Rating/Rating';
import { dateFormat } from 'utils/shared/utils';
import withErrorBoundary from 'components/shared/withErrorBoundary';

interface PackageOfflineDetailsProps {
  deal: DealModel;
  choosenRoom?: PckRoom;
}

const PackageOfflineDetails: FC<PackageOfflineDetailsProps> = ({ deal, ...props }) => {
  const [t] = useTranslation();

  const getRoom = () => {
    if (deal.package.oneItemPrice.listOfpckRoomTypeList?.[0]?.pckRooms?.length) {
      const minPrice = Math.min(
        ...deal.package.oneItemPrice.listOfpckRoomTypeList?.[0].pckRooms.map(
          (room) => room.room?.bruto
        )
      );

      const cheapestRoom = deal.package.oneItemPrice.listOfpckRoomTypeList?.[0].pckRooms.find(
        (room) => room.room?.bruto === minPrice
      )!;

      const choosenRoom = props.choosenRoom || cheapestRoom;

      return choosenRoom;
    }
  };

  return (
    <div className="default-shadow flight-details-item">
      <div className="title-underline text-lg mb-15">
        {deal.package.productType === 'FLTM' ? t('package.flight') : t('package.details')}
      </div>
      <div className="flight-details-info">
        <div className="package-details-block">
          <span className="package-details-title">חבילה</span>
          <span className="package-details-text">{deal.package.nameLang?.[0]?.desc}</span>
        </div>
        <div className="package-details-block">
          <span className="package-details-title">{t('flight.departure_date')}</span>
          <span className="package-details-text">
            {dateFormat(deal.package.startDate, 'EEE - dd MMMM, yyyy')}
          </span>
        </div>

        <div></div>
        <div className="package-details-block">
          <span className="package-details-title">{t('flight.return_date')}</span>
          {deal.package.productType === 'FLTM' ? (
            <span className="package-details-text">
              {dateFormat(deal.package.flightInBound.date, 'EEE - dd MMMM, yyyy')}
            </span>
          ) : (
            <span className="package-details-text">
              {dateFormat(
                addDays(deal.package.numberOfNights, new Date(deal.package.startDate)),
                'EEE - dd MMMM, yyyy'
              )}
            </span>
          )}
        </div>

        {deal.package.productType !== 'FLTM' && (
          <div className="package-details-block">
            <span className="package-details-title">{t('package.nights')}</span>
            <span className="package-details-text">
              {deal.package.numberOfNights} {t('hotel.nights_up')}
            </span>
          </div>
        )}

        <div className="package-details-block">
          <span className="package-details-title">{t('flight.from')}</span>
          <span className="package-details-text">
            <span>
              {deal.departureAirport.airCityLang.desc} - {deal.departureAirport.code}{' '}
            </span>
            <span className="text-normal">{deal.departureAirport.desc}</span>
          </span>
        </div>
        <div className="package-details-block">
          <span className="package-details-title">{t('flight.to')}</span>
          <span className="package-details-text">
            <span>
              {deal.outboundArrivalCity.cityDescLang_1} - {deal.package.flightOutBound.arrivalIATA}{' '}
            </span>
          </span>
        </div>
        {deal.package.productType !== 'FLTM' && (
          <div className="package-details-block">
            <span className="package-details-title">{t('hotel.hotel')}</span>
            <span className="package-details-text text-cap">
              <span>{deal.package.hoteltDesc}</span>
              <Rating value={deal.package.star} className="package-details-rating" />
            </span>
          </div>
        )}

        <div className="package-details-block">
          <span className="package-details-title">{t('filter.airline')}</span>
          <span className="package-details-text">
            {deal.departureAirline.desc} / {deal.arrivalAirline.desc}
          </span>
        </div>
        {!!deal.package.flightOutBound.flightDuration && (
          <div className="package-details-block">
            <span className="package-details-title">{t('filter.duration')}</span>
            <span className="package-details-text">
              {deal.package.flightOutBound.flightDuration}
              {' / '}
              {deal.package.flightInBound.flightDuration}
            </span>
          </div>
        )}
        {!!getRoom() && (
          <div className="package-details-block">
            <span className="package-details-title">{t('package.occupancy')}</span>
            <span className="package-details-text">{getRoom()?.room.desc}</span>
          </div>
        )}

        <div className="package-details-block">
          <span className="package-details-title">{t('flight.total_price')} </span>
          <span className="package-details-text">
            {formatPriceCurrency(
              deal.package.productType === 'FLTM'
                ? deal.package.oneItemPrice.flightOneAdult.bruto
                : getRoom()?.room.bruto || 0,
              getCurrency(deal.currency)
            )}
          </span>
        </div>
      </div>
    </div>
  );
};

export default withErrorBoundary(PackageOfflineDetails, 'PackageOfflineDetails');
